'use client';

import React, { FC } from 'react';
import { Link } from '@/navigation';
import useAuth from '@/context/AuthContext';
import {
  TypeGlobalNavFields,
  TypeLink_externalFields,
  TypeLink_internal,
} from '@/core/generated-types';
import { slugBuilder } from '@/core/contentfulConfig';
import { CONTENT_TYPE_LINK } from '@/core/constants';
import { useUserMenuLinks } from '../../hooks/useUserMenuLinks';
import { Translations } from '@/utils/translations/generateTranslationObject';
import { GenericAccordion } from '../../GenericAccordion/GenericAccordion';
import { UserIcon } from '../../UserIcon/UserIcon';

export interface UserMenuProps {
  signInLink: TypeGlobalNavFields['signInLink'];
  linkMenuLoggedIn: TypeGlobalNavFields['linkMenuLoggedIn'];
  translations: Translations;
}

export const UserMenu: FC<UserMenuProps> = ({
  signInLink,
  linkMenuLoggedIn,
  translations,
}) => {
  const { user } = useAuth();
  const { hardcodedLinks } = useUserMenuLinks(translations);

  // Unauthenticated user
  if (!user) {
    const isExternalSignInLink =
      signInLink.sys.contentType.sys.id === CONTENT_TYPE_LINK.EXTERNAL;
    const signInHref = isExternalSignInLink
      ? (signInLink.fields as TypeLink_externalFields).linkUrl || '/'
      : slugBuilder(signInLink as TypeLink_internal);

    return (
      <div className="px-4">
        <Link
          href={signInHref}
          className="w-full py-4 text-left flex items-center text-lg text-charcoal gap-3"
        >
          <span>Sign in</span>
        </Link>
      </div>
    );
  }

  // Authenticated user
  return (
    <GenericAccordion
      className="border-b border-transparent last:border-b-0 px-4"
      header={
        <div className="flex items-center">
          <UserIcon />
          <span className="ml-3">
            {translations.hello || 'Hello'}, {user.attributes?.name || 'User'}
          </span>
        </div>
      }
      headerClassName="text-base w-full py-4 text-left flex items-center justify-between text-charcoal pr-1.5"
      transitionDuration={300}
      maxHeight="1000px"
    >
      <div className="pl-[40px] pb-2 flex flex-col gap-3">
        {/* Hardcoded links first */}
        {hardcodedLinks?.map((link) => (
          <Link
            key={link.id}
            href={link.href}
            onClick={link.onClick}
            className="hover:text-blue-deep"
          >
            {link.label}
          </Link>
        ))}

        {/* Contentful links after hardcoded links */}
        {linkMenuLoggedIn?.map((link) => {
          const isExternalLink =
            link.sys.contentType.sys.id === CONTENT_TYPE_LINK.EXTERNAL;
          const href = isExternalLink
            ? (link.fields as any).linkUrl || '#'
            : slugBuilder(link as any);

          return (
            <Link
              key={link.sys.id}
              href={href}
              className="hover:text-blue-deep text-sm"
            >
              {link.fields.linkText}
            </Link>
          );
        })}
      </div>
    </GenericAccordion>
  );
};

export default React.memo(UserMenu);
